import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { AccountHeader } from '@mobi/component-library/Account'
import { Button, Input } from '@mobi/component-library/Common/V2'
import { newTheme as theme } from '@mobi/component-library/Theme'
import { DocumentPermission } from '@mobi/account/Areas/SignUp'
import { FormLayout, DocumentBodyStyled } from '../'
import { spacing } from '@mobi/component-library/Theme/Common'
import { DropdownInput } from '@mobi/component-library/Selection&Input'
import { MedicareCardColor } from '@mobi/api-types/src/account/signup'
import {
  getNameValidationMessage,
  medicareExpiryNext10Years,
  NUMERIC_REGEX,
  getRequiredValidationMessage,
  validateCardNumber,
  validateReferenceNumber,
  validateExpiryDay,
  isValidName,
} from '@mobi/account/Areas/SignUp/Utils/FormValidation'
import { months } from '@mobi/utils/date'

const cardColours = [
  { value: '', label: 'Select Colour' },
  { value: MedicareCardColor.Green.toString(), label: 'Green' },
  { value: MedicareCardColor.Yellow.toString(), label: 'Yellow' },
  { value: MedicareCardColor.Blue.toString(), label: 'Blue' },
]

type MedicareProps = {
  onSubmit: () => void
  isBusy: boolean
  cardNumber: string
  setCardNumber: (value: string) => void
  referenceNumber: string
  setReferenceNumber: (value: string) => void
  cardColor: MedicareCardColor | null
  setCardColor: (value: MedicareCardColor | null) => void
  expiryDay: number | undefined
  setExpiryDay: (value: number | undefined) => void
  expiryMonth: string
  setExpiryMonth: (value: string) => void
  expiryYear: number | undefined
  setExpiryYear: (value: number | undefined) => void
  middleName?: string
  setMiddleName?: (value: string) => void
}

export const Medicare = ({
  onSubmit,
  isBusy,
  cardNumber,
  setCardNumber,
  referenceNumber,
  setReferenceNumber,
  cardColor,
  setCardColor,
  expiryDay,
  setExpiryDay,
  expiryMonth,
  setExpiryMonth,
  expiryYear,
  setExpiryYear,
  middleName,
  setMiddleName,
}: MedicareProps) => {
  const [givePermission, setGivePermission] = useState(false)
  const [validationMessages, setValidationMessages] = useState<Record<string, string>>({})

  const isGreenMedicareCardSelected = cardColor === MedicareCardColor.Green

  const isMedicareCardExpiryValid =
    (isGreenMedicareCardSelected && !!expiryMonth && !!expiryYear) ||
    (!isGreenMedicareCardSelected && !!expiryDay && !!expiryMonth && !!expiryYear)

  const days = !isGreenMedicareCardSelected
    ? Array.from({ length: daysInMonth(Number(expiryYear), parseInt(expiryMonth)) }, (_, i) => ({
        value: String(i + 1),
        label: String(i + 1),
      }))
    : []

  useEffect(() => {
    if (
      (expiryDay && expiryDay > daysInMonth(Number(expiryYear), parseInt(expiryMonth))) ||
      isGreenMedicareCardSelected
    ) {
      setExpiryDay(undefined)
    }

    if (!isMedicareCardExpiryValid) {
      setValidationMessages(prev => ({ ...prev, cardExpiryError: '' }))
    } else {
      const tempDay = isGreenMedicareCardSelected ? 31 : expiryDay
      const monthIndex = parseInt(expiryMonth) - 1

      const expiryDate = new Date(Number(expiryYear), monthIndex, tempDay)
      const todayTemp = new Date()
      const today = new Date(todayTemp.getFullYear(), todayTemp.getMonth(), todayTemp.getDate())
      setValidationMessages(prev => ({
        ...prev,
        cardExpiryError: expiryDate < today ? 'Enter a valid expiry date.' : '',
      }))
    }
  }, [expiryDay, expiryMonth, expiryYear, isGreenMedicareCardSelected, isMedicareCardExpiryValid])

  const hasValidEntries = Object.values(validationMessages).every(error => error === '')
  const allowToProceed =
    hasValidEntries &&
    givePermission &&
    (!middleName || isValidName(middleName)) &&
    !!cardNumber &&
    !!referenceNumber &&
    cardColor !== undefined &&
    (isGreenMedicareCardSelected || !!expiryDay) &&
    !!expiryMonth &&
    !!expiryYear

  return (
    <FormLayout>
      <AccountHeader
        alignItems='start'
        title='Enter your Medicare details'
        description='We need these details to be able to verify your identity'
        style={{ marginBottom: '0rem' }}
      />
      <DocumentBodyStyled>
        <Input
          name='medicare-middle-name'
          label='Middle name (if applicable)'
          placeholder='Enter exactly as shown on Medicare card'
          isDisabled={isBusy}
          value={middleName}
          onChange={e => {
            if (setMiddleName) setMiddleName(e.target.value)
            setValidationMessages(prev => ({ ...prev, middleName: '' }))
          }}
          onBlur={() =>
            setValidationMessages(prev => ({
              ...prev,
              middleName: getNameValidationMessage(middleName || '', true),
            }))
          }
          isInvalid={!!validationMessages.middleName}
          helpText={validationMessages.middleName}
        />
        <Input
          name='medicare-card-number'
          label='Medicare card number'
          placeholder='Enter card number'
          isDisabled={isBusy}
          value={cardNumber}
          onChange={e => {
            setCardNumber(e.target.value.replace(NUMERIC_REGEX, ''))
            setValidationMessages(prev => ({ ...prev, cardNumber: '' }))
          }}
          onBlur={() =>
            setValidationMessages(prev => ({ ...prev, cardNumber: validateCardNumber(cardNumber) }))
          }
          isInvalid={!!validationMessages.cardNumber}
          helpText={validationMessages.cardNumber}
          maxLength={10}
          inputMode='numeric'
          pattern='[0-9]*'
        />
        <Input
          name='medicare-reference-number'
          label='Medicare reference number'
          placeholder='Enter reference number'
          isDisabled={isBusy}
          value={referenceNumber}
          onChange={e => {
            setReferenceNumber(e.target.value.replace(NUMERIC_REGEX, ''))
            setValidationMessages(prev => ({ ...prev, referenceNumber: '' }))
          }}
          onBlur={() =>
            setValidationMessages(prev => ({
              ...prev,
              referenceNumber: validateReferenceNumber(referenceNumber),
            }))
          }
          isInvalid={!!validationMessages.referenceNumber}
          helpText={validationMessages.referenceNumber}
          type='text'
          maxLength={1}
          inputMode='numeric'
          pattern='[0-9]*'
        />
        <DropdownInput
          name='medicare-card-colour'
          label='Card colour'
          placeholder='Select colour'
          value={cardColor === null ? '' : cardColor.toString()}
          isDisabled={isBusy}
          onChange={event => {
            const val = event.target.value
            setCardColor(val ? parseInt(val) : null)
            setValidationMessages(prev => ({ ...prev, cardColor: '' }))
          }}
          onBlur={() =>
            setValidationMessages(prev => ({
              ...prev,
              cardColor: getRequiredValidationMessage(cardColor?.toString()),
            }))
          }
          isInvalid={!!validationMessages.cardColor}
          helpText={validationMessages.cardColor}
        >
          {cardColours.map(colour => (
            <option key={colour.value} value={colour.value} disabled={colour.value === ''}>
              {colour.label}
            </option>
          ))}
        </DropdownInput>
        <div>
          <LabelStyled>Expiry date</LabelStyled>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: spacing.smx1,
              width: '100%',
            }}
          >
            {!isGreenMedicareCardSelected && (
              <DropdownInput
                name='medicare-expiry-day'
                placeholder='Day'
                value={expiryDay?.toString() || ''}
                isDisabled={isBusy}
                onChange={e => {
                  setExpiryDay(parseInt(e.target.value) || undefined)
                  setValidationMessages(prev => ({ ...prev, expiryDay: '' }))
                }}
                onBlur={() =>
                  setValidationMessages(prev => ({
                    ...prev,
                    expiryDay: validateExpiryDay(expiryDay, isGreenMedicareCardSelected),
                  }))
                }
                isInvalid={!!validationMessages.expiryDay}
                helpText={validationMessages.expiryDay}
                style={{ flex: 1 }}
              >
                <option value=''>Day</option>
                {days.map(d => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </DropdownInput>
            )}
            <DropdownInput
              name='medicare-expiry-month'
              placeholder='Month'
              value={expiryMonth}
              isDisabled={isBusy}
              onChange={e => {
                setExpiryMonth(e.target.value)
                setValidationMessages(prev => ({ ...prev, expiryMonth: '' }))
              }}
              onBlur={() =>
                setValidationMessages(prev => ({
                  ...prev,
                  expiryMonth: getRequiredValidationMessage(expiryMonth),
                }))
              }
              isInvalid={!!validationMessages.expiryMonth}
              helpText={validationMessages.expiryMonth}
              style={{ flex: 1 }}
            >
              <option value=''>Month</option>
              {months.map(m => (
                <option key={m.number} value={m.number}>
                  {m.name}
                </option>
              ))}
            </DropdownInput>
            <DropdownInput
              name='medicare-expiry-year'
              placeholder='Year'
              value={expiryYear?.toString() || ''}
              isDisabled={isBusy}
              onChange={e => {
                setExpiryYear(parseInt(e.target.value) || undefined)
                setValidationMessages(prev => ({ ...prev, expiryYear: '' }))
              }}
              onBlur={() =>
                setValidationMessages(prev => ({
                  ...prev,
                  expiryYear: getRequiredValidationMessage(expiryYear?.toString()),
                }))
              }
              isInvalid={!!validationMessages.expiryYear}
              helpText={validationMessages.expiryYear}
              style={{ flex: 1 }}
            >
              <option value=''>Year</option>
              {medicareExpiryNext10Years().map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </DropdownInput>
          </div>
        </div>
        {validationMessages.cardExpiryError && (
          <div style={{ color: 'red', marginTop: '0.5rem' }}>
            {validationMessages.cardExpiryError}
          </div>
        )}
        <div style={{ paddingTop: spacing.smx1 }}>
          <DocumentPermission isBusy={isBusy} onChange={e => setGivePermission(e.target.checked)} />
        </div>
      </DocumentBodyStyled>
      <footer>
        <Button isFullWidth disabled={!allowToProceed} isLoading={isBusy} onClick={onSubmit}>
          Continue
        </Button>
      </footer>
    </FormLayout>
  )
}

// =============
// Local Helpers
// =============

function daysInMonth(year: number, month: number) {
  if (!month || month < 1 || month > 12) {
    return 31
  }
  const currentYear = new Date().getFullYear()

  if (!year || year < currentYear) {
    year = currentYear
  }

  //day 0 gives us number of days in previous month
  return new Date(year, month, 0).getDate()
}

// ======
// Styles
// ======

const LabelStyled = styled.label({
  display: 'block',
  fontWeight: theme.font.weight.medium,
  ...theme.font.size.lg,
  marginBottom: theme.components.Input.spacing.label.margin.bottom,
})
